/**
 * Application Configuration
 * Static settings and server endpoints
 */

import { Capacitor } from '@capacitor/core';

// Platform Detection
export const PLATFORM = {
  isNative: Capacitor.isNativePlatform(),
  isAndroid: Capacitor.getPlatform() === 'android',
  isIOS: Capacitor.getPlatform() === 'ios',
  isWeb: Capacitor.getPlatform() === 'web',
  platform: Capacitor.getPlatform(),
};

// Platform-specific UI Configuration
export const PLATFORM_UI_CONFIG = {
  // Status bar padding for native apps (in pixels)
  statusBarPadding: {
    android: 48, // Android status bar height
    ios: 48,     // iOS status bar + notch
    web: 0,      // No padding for web
  },

  bottomNavBarPadding: {
    android: 16,
    ios: 0,    // iOS home indicator area
    web: 0,
  },
  
  // Get the appropriate padding for current platform
  getStatusBarPadding: (): number => {
    if (PLATFORM.isAndroid) return PLATFORM_UI_CONFIG.statusBarPadding.android;
    if (PLATFORM.isIOS) return PLATFORM_UI_CONFIG.statusBarPadding.ios;
    return PLATFORM_UI_CONFIG.statusBarPadding.web;
  },

  getBottomNavBarPadding: (): number => {
    if (PLATFORM.isAndroid) return PLATFORM_UI_CONFIG.bottomNavBarPadding.android;
    if (PLATFORM.isIOS) return PLATFORM_UI_CONFIG.bottomNavBarPadding.ios;
    return PLATFORM_UI_CONFIG.bottomNavBarPadding.web;
  }
};

// Server API Configuration
export const API_CONFIG = {
  // Base URL for the API server
  // 127.0.0.1:8000 for local development
  BASE_URL: process.env.REACT_APP_API_URL || 'https://myaquafood.nstu.edu.bd',
  
  // Help Manual URL
  HELP_URL: 'https://myaquafood-app.nstu.edu.bd/help',
  
  // API Endpoints
  ENDPOINTS: {
    // Authentication
    LOGIN: '/api/v1/auth/login',
    REGISTER: '/api/v1/auth/register',
    CHECK_USERNAME: '/api/v1/auth/check-username',
    LOGOUT: '/api/v1/auth/logout',
    REFRESH: '/api/v1/auth/refresh',
    
    // User
    PROFILE: '/api/v1/auth/user',
    UPDATE_PROFILE: '/api/v1/auth/user',
    DELETE_ACCOUNT: '/api/v1/auth/user',
    
    // Sync
    SYNC_UPLOAD: '/api/v1/sync/push',
    SYNC_DOWNLOAD: '/api/v1/sync/pull',
    SYNC_STATUS: '/api/v1/sync/status',
    SYNC_RECONCILE: '/api/v1/sync/reconcile',
    
    // Persons/Respondents
    PERSONS: '/api/v1/persons',
    PERSON_DETAIL: '/api/v1/persons/:id',
    
    // Channels
    CHANNELS: '/api/v1/channels',
  },
  
  // Request timeout in milliseconds
  TIMEOUT: 30000,
  
  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY: 1000, // milliseconds
  },
};

// Application Settings
export const APP_CONFIG = {
  // App version
  VERSION: '1.0.0',
  
  // App name - easily changeable for rebranding
  NAME: 'MyAquaFood',
  
  // Default language
  DEFAULT_LANGUAGE: 'en' as const,
  
  // Default weight unit
  DEFAULT_WEIGHT_UNIT: 'gm' as const,
  
  // Default mode
  DEFAULT_MODE: 'user' as const,
  
  // Data version for schema changes
  DATA_VERSION: 1,
  
  // Storage keys
  STORAGE_KEYS: {
    USER_DATA: 'fishAppData',
    AUTH_TOKEN: 'fishAppAuthToken',
    PIN_DATA: 'fishAppPinData',
    SETTINGS: 'fishAppSettings',
    LAST_SYNC: 'fishAppLastSync',
    LAST_LOGIN_USER_ID: 'fishAppLastLoginUserId',
    // Session-only (stored in sessionStorage)
    SESSION_UNLOCKED: 'fishAppSessionUnlocked',
  },
};

// Sync Configuration
export const SYNC_CONFIG = {
  // Default sync interval in minutes
  DEFAULT_INTERVAL: 60,
  
  // Minimum sync interval in minutes
  MIN_INTERVAL: 5,
  
  // Maximum sync interval in minutes
  MAX_INTERVAL: 1440, // 24 hours
  
  // Auto sync on app start
  AUTO_SYNC_ON_START: true,
  
  // Auto sync when online
  AUTO_SYNC_WHEN_ONLINE: true,
};

// Security Configuration
export const SECURITY_CONFIG = {
  // PIN length
  PIN_LENGTH: 4,
  
  // PIN retry attempts before lockout
  MAX_PIN_ATTEMPTS: 5,
  
  // Lockout duration in minutes
  LOCKOUT_DURATION: 5,
  
  // Session timeout in minutes
  SESSION_TIMEOUT: 60,
  
  // Token validation cache duration in minutes
  // Cached token validation results are reused within this window to avoid redundant network calls
  TOKEN_VALIDATION_CACHE_MINUTES: 5,
  
  // Skip server fetch for PersonRecord when local record exists during fast authentication
  // Improves PIN login speed by deferring server sync to background
  SKIP_PERSON_FETCH_ON_PIN_LOGIN: true,
};

// UI Configuration
export const UI_CONFIG = {
  // Splash screen duration in milliseconds
  SPLASH_DURATION: 1500,
  
  // Animation durations
  ANIMATION: {
    FAST: 200,
    NORMAL: 300,
    SLOW: 500,
  },
  
  // Toast duration in milliseconds
  TOAST_DURATION: 3000,
};

// Recipe Image Configuration
export const RECIPE_IMAGE_CONFIG = {
  // Base directory path for recipe images (relative to public/assets)
  BASE_PATH: 'recipe_images_webp',
  
  // Image file extension (without dot)
  // Options: 'png', 'jpg', 'webp'
  FILE_EXTENSION: 'webp' as const,
  
  // Full base URL path
  get FULL_PATH() {
    return `/assets/${this.BASE_PATH}`;
  },
};
